export const isWebExtension = (): boolean => {
  // Check if we're running in an iframe
  const isIframe = window.self !== window.top;

  // Check if we have access to chrome.runtime
  const hasChromeRuntime = typeof chrome !== "undefined" && chrome.runtime;

  // Check ancestor origins for chrome-extension:// protocol
  const hasExtensionAncestor = (() => {
    try {
      return Array.from(location.ancestorOrigins).some(origin => 
        origin.startsWith('chrome-extension://')
      );
    } catch (e) {
      return false;
    }
  })();

  return isIframe && hasChromeRuntime && hasExtensionAncestor;
};

export const isMobileApp = (): boolean => {
  return typeof window !== "undefined" && (window as any).ReactNativeWebView;
};

export type MobilePlatform = "ios" | "android";

/**
 * The wrapper's tag, as `<Product>App-<Platform>`.
 *
 * Each mobile product stamps its own name — `BoilitApp-iOS`,
 * `ZairaiApp-Android` — so a generic pattern is required rather than a
 * hardcoded list.  The legacy `SharpanaApp-*` tag matches this shape too, and
 * MUST keep matching: binaries shipped before the product split stay on
 * users' phones for months and still identify themselves that way.
 */
const UA_TAG = /([A-Za-z0-9]+)App-(iOS|Android)/;

/** Both places the wrapper advertises itself, most reliable first. */
const uaTagSources = (): string[] => {
  const sources: string[] = [];
  try {
    const injected = (window as any).SHARPANA_UA_TAG as string | undefined;
    if (injected) sources.push(injected);
  } catch {}
  try {
    if (navigator.userAgent) sources.push(navigator.userAgent);
  } catch {}
  return sources;
};

export const getMobileAppPlatform = (): MobilePlatform | null => {
  if (typeof window === "undefined") return null;
  for (const source of uaTagSources()) {
    const match = UA_TAG.exec(source);
    if (match) return match[2] === "iOS" ? "ios" : "android";
  }
  return null;
};

/**
 * Which product's wrapper is hosting us, e.g. `"Boilit"`, `"Zairai"`, or
 * `"Sharpana"` from a pre-split binary.  Null on the web.
 */
export const getMobileAppProduct = (): string | null => {
  if (typeof window === "undefined") return null;
  for (const source of uaTagSources()) {
    const match = UA_TAG.exec(source);
    if (match) return match[1];
  }
  return null;
};